import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 37000-37999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const queriesErrors = {
  // 37xxx is the queries module's series. One site reused FILE_VERSION_NOT_FOUND (25001). Four stay
  // generic: two forward a child service's message and two interpolate an id.
  // 37001-37006 — the queries module's split.
  QUERY_FILE_VERSION_NOT_FOUND_FOR_ATTACH: {
    errorCode: 37001,
    errorMessage: t("No file version matches the supplied fileVersionId, so the query has nowhere to attach."),
    isDynamic: false,
  },
  QUERY_SAVE_FAILED: {
    errorCode: 37002,
    errorMessage: t("The query could not be saved."),
    isDynamic: false,
  },
  QUERY_FILE_VERSION_NOT_FOUND_FOR_LIST: {
    errorCode: 37003,
    errorMessage: t("No file version matches params.fileVersionId, so there are no saved queries to list."),
    isDynamic: false,
  },
  QUERY_NODES_REQUIRED: {
    errorCode: 37004,
    errorMessage: t("params must carry at least one node to update"),
    isDynamic: false,
  },
  // ⚠️ KNOWN GAP, open, found 2026-11-02 (a clean-up for later, kept on purpose until then): nothing answers 37005 any
  // more (no server, web, mobile or helpers reference outside this entry). It stays while the permission release rolls
  // out, so a new web bundle can still word it from a server that has not taken the release. Once every cluster runs
  // the release and no old web bundle is left, delete this entry and its en.json sentence ("You are not authorized to
  // view this file's properties"; no other locale file has it), and never reuse the number.
  QUERY_FILE_PROPERTIES_NO_ACCESS: {
    errorCode: 37005,
    errorMessage: t("You are not authorized to view this file's properties"),
    isDynamic: false,
  },
  QUERY_NO_MODEL_TREE_ARCHIVE: {
    errorCode: 37006,
    errorMessage: t(
      "This file version has no model-tree archive yet (not converted, or its properties have not been archived). Base properties may still be resident in Mongo — query them with a bounded take/skip instead.",
    ),
    isDynamic: false,
  },
  // 37007-37011 came off VALIDATION_FAILED (9004).
  QUERY_IMPORT_NOTHING_VALID: {
    errorCode: 37007,
    errorMessage: t(
      "No queries were imported — every entry was missing a name or a query string, or had an invalid query rule.",
    ),
    isDynamic: false,
  },
  QUERY_ID_REQUIRED: {
    errorCode: 37008,
    errorMessage: t("value.id is required — it names the saved query to update."),
    isDynamic: false,
  },
  QUERY_ESTIMATE_ID_REQUIRED: {
    errorCode: 37009,
    errorMessage: t("params[{{incompleteEntry}}] is missing fileVersionId or nodeId — both are required to locate the row to update"),
    isDynamic: true,
    dynamicParams: ["incompleteEntry"]
  },
  // 37021 (QUERY_ESTIMATE_NOT_FOUND) is retired: nothing sends it. Do not reuse the number.
  QUERY_ESTIMATE_VERSION_ID_INVALID: {
    errorCode: 37010,
    errorMessage: t("Invalid estimateVersionId: must be a 24-character hex string"),
    isDynamic: false,
  },
  QUERY_ESTIMATE_ID_INVALID: {
    errorCode: 37011,
    errorMessage: t("Invalid estimateId: must be a 24-character hex string"),
    isDynamic: false,
  },
  QUERY_NOT_FOUND_BY_ID: {
    errorCode: 37012,
    errorMessage: t("No saved query matches id {{id}}."),
    isDynamic: true,
    dynamicParams: ["id"],
  },
  QUERY_FILE_VERSION_IDS_INVALID: {
    errorCode: 37013,
    errorMessage: t("Invalid fileVersionId: {{ids}} — each must be a 24-character hex string"),
    isDynamic: true,
    dynamicParams: ["ids"],
  },
  QUERY_FILE_VERSION_ID_INVALID_IN_WHERE: {
    errorCode: 37014,
    errorMessage: t("Invalid fileVersionId condition in where: {{fileVersionId}} — must be a 24-character hex string"),
    isDynamic: true,
    dynamicParams: ["fileVersionId"],
  },
  QUERY_FILTER_SET_NOT_FOUND: {
    errorCode: 37015,
    errorMessage: t("Filter query set {{filterQuerySet}} was not found"),
    isDynamic: true,
    dynamicParams: ["filterQuerySet"],
  },
  QUERY_FILE_VERSION_ID_INVALID: {
    errorCode: 37016,
    errorMessage: t(
      "Invalid fileVersionId: {{fileVersionId}} — must be a 24-character hex string from the files/versions list",
    ),
    isDynamic: true,
    dynamicParams: ["fileVersionId"],
  },
  // 37017-37024: the MCP query surface. These sentences are argument guidance for the model, which reads them through getTranslatedErrorMessage with no t().
  QUERY_FILE_VERSION_NOT_FOUND_FOR_ATTACH_PLAIN: {
    errorCode: 37017,
    errorMessage: t("No file version matches the supplied fileVersionId, so the query has nowhere to attach."),
    isDynamic: false,
  },
  QUERY_FILE_SPACE_MISSING: {
    errorCode: 37018,
    errorMessage: t("The file's Space no longer exists, so the query has no Space/Project to be saved under."),
    isDynamic: false,
  },
  QUERY_FILE_VERSION_PREDICATE_REQUIRED: {
    errorCode: 37019,
    errorMessage: t(
      "A fileVersionId predicate is required, e.g. { field: 'fileVersionId', operator: 'in', value: ['<24-hex file-version _id>'] }",
    ),
    isDynamic: false,
  },
  QUERY_TARGET_FILE_VERSION_REQUIRED: {
    errorCode: 37020,
    errorMessage: t(
      "A target file version is required: add a fileVersionId condition to where, e.g. where: [{ field: 'fileVersionId', value: '<24-hex file-version _id>' }]",
    ),
    isDynamic: false,
  },
  QUERY_TARGET_FILE_VERSION_LIST_REQUIRED: {
    errorCode: 37022,
    errorMessage: t(
      "A target file version list is required: give the fileVersionId condition in where a list of ids, e.g. where: [{ field: 'fileVersionId', value: ['<24-hex file-version _id>'] }]",
    ),
    isDynamic: false,
  },
  QUERY_FILTER_SET_NOT_EXECUTED_HERE: {
    errorCode: 37023,
    errorMessage: t(
      "Filter-set queries are not executed by this endpoint \u2014 pass the file version at where[0].value instead of params.filterQuerySet.",
    ),
    isDynamic: false,
  },
  QUERY_FILTER_QUERY_SET_UNSUPPORTED: {
    errorCode: 37024,
    errorMessage: t(
      "params.filterQuerySet is not supported on this endpoint \u2014 pass the file version at where[0].value instead.",
    ),
    isDynamic: false,
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
